import { useEffect, useRef, useState } from 'react';

interface VideoItem {
  url: string;
  title?: string;
  description?: string;
}

interface VideoCarouselProps {
  items: VideoItem[];
  autoPlayActive?: boolean;
  startMuted?: boolean;
}

export default function VideoCarousel({ items, autoPlayActive = true, startMuted = true }: VideoCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(startMuted);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      video.muted = isMuted;
      if (index === currentIndex) {
        if (autoPlayActive) {
          video.play().catch(() => {});
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentIndex, autoPlayActive, isMuted]);

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className="max-w-4xl mx-auto mb-16">
      <div className="bg-white rounded-3xl shadow-2xl p-2">
        <div className="relative aspect-video rounded-2xl overflow-hidden">
          <div
            className="flex h-full w-full transition-transform duration-500"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {items.map((item, index) => (
              <div key={index} className="min-w-full h-full">
                <video
                  ref={(el) => (videoRefs.current[index] = el)}
                  src={item.url}
                  className="w-full h-full object-cover"
                  muted={isMuted}
                  playsInline
                  loop
                  autoPlay={autoPlayActive && index === currentIndex}
                >
                  Tu navegador no soporta el elemento de video.
                </video>
              </div>
            ))}
          </div>

          {/* Controles */}
          <button
            type="button"
            aria-label="Anterior"
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#2C4358] rounded-full w-10 h-10 flex items-center justify-center shadow-md"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Siguiente"
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#2C4358] rounded-full w-10 h-10 flex items-center justify-center shadow-md"
          >
            ›
          </button>

          {/* Botón Sonido */}
          <button
            type="button"
            aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
            onClick={() => {
              const nextMuted = !isMuted;
              setIsMuted(nextMuted);
              const currentVideo = videoRefs.current[currentIndex];
              if (currentVideo) {
                currentVideo.muted = nextMuted;
                if (!autoPlayActive && !nextMuted) {
                  currentVideo.play().catch(() => {});
                }
              }
            }}
            className="absolute right-3 bottom-3 bg-black/80 hover:bg-black text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
          >
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M3 10v4h4l5 4V6L7 10H3zm13.59 2l2.12 2.12 1.41-1.41L18.41 12l1.71-1.71-1.41-1.41L17 10.59l-2.12-2.12-1.41 1.41L15.59 12l-2.12 2.12 1.41 1.41L17 13.41z"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M3 10v4h4l5 4V6L7 10H3zm10.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03zM14 3.23v2.06c2.89 1 5 3.77 5 6.71s-2.11 5.71-5 6.71v2.06c4.01-1.07 7-4.77 7-8.77s-2.99-7.7-7-8.77z"/>
              </svg>
            )}
          </button>

          {/* Indicadores */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {items.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Ir al slide ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  currentIndex === index ? 'bg-[#22B6B2] w-6' : 'bg-white/70'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Título y descripción */}
        {items[currentIndex] && (
          <div className="px-4 py-3">
            {items[currentIndex].title && (
              <p className="text-[#2C4358] font-semibold">{items[currentIndex].title}</p>
            )}
            {items[currentIndex].description && (
              <p className="text-[#85929e] text-sm">{items[currentIndex].description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


